import { useMutation, useQuery } from "@tanstack/react-query";
import { toPng } from "html-to-image";
import React, { useRef, useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, NavLink } from "react-router-dom";
import { toast } from "react-toastify";

import { EditLanguagesModal } from "./EditLanguagesModal";
import { LanguageTagsDisplay } from "./LanguageTagsDisplay";
import { PageTitle } from "../../components/Common/PageTitle";
import { ReviewRequestPrintPreview } from "../../components/FacilityDetailsComponents/ReviewRequestPrint/ReviewRequestPrintPreview";
import { useImpersonate } from "../../hooks/impersonate";
import { useMe } from "../../hooks/queries";
import { LocationData } from "../../models/LocationData";
import {
  LocationReviewRequestPrintsData,
  AVAILABLE_LANGUAGES,
  LocationReviewRequestPrintsSizeOptions,
  UpdateLocationReviewRequestPrintsDTO,
} from "../../models/LocationReviewRequestPrints";
import DataSvc from "../../services/dataSvc";

import "./styles.scss";

interface MatchParams {
  locationId: string;
  organizationId?: string;
}

type ReviewRequestPrintsPageProps = RouteComponentProps<MatchParams>;

const ReviewRequestPrintsPage: React.FC<ReviewRequestPrintsPageProps> = ({
  match,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`reviewRequestPrintsPage.${key}`);

  const { impersonateStatus } = useImpersonate();
  useMe();
  const locationId = match.params.locationId;
  const organizationId = match.params.organizationId;

  const [downloading, setDownloading] = useState<{
    card: boolean;
    a5: boolean;
    a4: boolean;
  }>({
    card: false,
    a5: false,
    a4: false,
  });

  // Each size has its own enabled languages
  const [sizeLanguages, setSizeLanguages] = useState<{
    card: string[];
    a5: string[];
    a4: string[];
  }>({
    card: [],
    a5: [],
    a4: [],
  });

  // Modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    size: "card" | "a5" | "a4" | null;
  }>({
    isOpen: false,
    size: null,
  });

  // Refs for the QR code canvases (for downloading QR only)
  const cardQrRef = useRef<HTMLCanvasElement>(null);
  const a5QrRef = useRef<HTMLCanvasElement>(null);
  const a4QrRef = useRef<HTMLCanvasElement>(null);

  // Refs for the print preview containers (for downloading full content)
  const cardPreviewRef = useRef<HTMLDivElement>(null);
  const a5PreviewRef = useRef<HTMLDivElement>(null);
  const a4PreviewRef = useRef<HTMLDivElement>(null);

  const { data: locationData } = useQuery<LocationData>(
    ["location", locationId],
    () => DataSvc.getLocation(locationId)
  );

  const {
    data: printsData,
    refetch,
    isLoading,
  } = useQuery<LocationReviewRequestPrintsData>(
    ["location-review-request-prints", locationId, impersonateStatus],
    () => DataSvc.getLocationReviewRequestPrints(Number(locationId))
  );

  const updateMutation = useMutation(
    (data: UpdateLocationReviewRequestPrintsDTO) =>
      DataSvc.updateLocationReviewRequestPrints(Number(locationId), data),
    {
      onSuccess: () => {
        toast.success(t("update_success"));
        refetch();
      },
      onError: () => {
        toast.error(t("update_error"));
      },
    }
  );

  React.useEffect(() => {
    if (printsData) {
      const getEnabledLanguages = (
        sizeOptions: LocationReviewRequestPrintsSizeOptions
      ): string[] => {
        const enabled: string[] = [];
        if (sizeOptions.messages) {
          Object.entries(sizeOptions.messages).forEach(
            ([lang, langMessage]) => {
              if (langMessage.enabled) {
                enabled.push(lang);
              }
            }
          );
        }
        return enabled;
      };

      setSizeLanguages({
        card: getEnabledLanguages(printsData.card_size_options),
        a5: getEnabledLanguages(printsData.a_five_size_options),
        a4: getEnabledLanguages(printsData.a_four_size_options),
      });
    }
  }, [printsData]);

  const handleLanguageSave = (
    size: "card" | "a5" | "a4",
    languages: string[]
  ) => {
    setSizeLanguages((prev) => ({
      ...prev,
      [size]: languages,
    }));

    const sizeKeyMap = {
      card: "card_size_options",
      a5: "a_five_size_options",
      a4: "a_four_size_options",
    } as const;

    const payload: UpdateLocationReviewRequestPrintsDTO = {
      [sizeKeyMap[size]]: {
        enabled_languages: languages,
      },
    };

    updateMutation.mutate(payload);
  };

  const handleDownloadQR = async (
    size: "card" | "a5" | "a4",
    previewRef: React.RefObject<HTMLDivElement>
  ) => {
    if (!previewRef.current || !locationData) return;

    setDownloading((prev) => ({ ...prev, [size]: true }));

    try {
      const printContent = previewRef.current.querySelector(
        ".print-content"
      ) as HTMLElement;

      if (!printContent) {
        throw new Error("Print content not found");
      }

      // Calculate pixel dimensions at 300 DPI
      // 1 inch = 25.4mm, 1 inch = 300 pixels at 300 DPI
      // So: pixels = mm * (300 / 25.4) = mm * 11.811
      const DPI = 300;
      const MM_TO_INCH = 25.4;
      const MM_TO_PX = DPI / MM_TO_INCH;

      const dimensions: Record<string, { width: number; height: number }> = {
        card: {
          width: Math.ceil(91 * MM_TO_PX),
          height: Math.floor(55 * MM_TO_PX),
        },
        a5: {
          width: Math.ceil(210 * MM_TO_PX),
          height: Math.floor(148 * MM_TO_PX),
        },
        a4: {
          width: Math.ceil(297 * MM_TO_PX),
          height: Math.floor(210 * MM_TO_PX),
        },
      };

      const targetDimensions = dimensions[size];

      // Get current DOM dimensions
      const currentWidth = printContent.offsetWidth;

      // Calculate pixelRatio based on width (height will scale proportionally due to aspectRatio)
      const pixelRatio = targetDimensions.width / currentWidth;

      const dataUrl = await toPng(printContent, {
        quality: 1.0,
        pixelRatio: pixelRatio,
      });

      const link = document.createElement("a");
      link.download = `${locationData.name}_review_request_${size}.png`;
      link.href = dataUrl;
      link.click();

      ReactGA.event({
        category: "review-request-prints",
        action: `Download Print ${size}`,
      });

      toast.success(t("download_success"));
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(t("download_error"));
    } finally {
      setDownloading((prev) => ({ ...prev, [size]: false }));
    }
  };

  const qrCodeUrl = locationData
    ? `https://search.google.com/local/writereview?placeid=${locationData.gbp_place_id}`
    : "";

  const getFilteredMessages = (
    sizeOptions: LocationReviewRequestPrintsSizeOptions,
    size: "card" | "a5" | "a4"
  ): LocationReviewRequestPrintsSizeOptions => {
    const messages: { [key: string]: { enabled: boolean; message: string } } =
      {};

    if (sizeOptions.messages) {
      Object.entries(sizeOptions.messages).forEach(([lang, langMessage]) => {
        if (sizeLanguages[size].includes(lang) && langMessage.enabled) {
          messages[lang] = langMessage;
        }
      });
    }

    return { messages };
  };

  const getLanguageTags = (
    sizeOptions: LocationReviewRequestPrintsSizeOptions,
    size: "card" | "a5" | "a4"
  ) => {
    const enabledLangs = sizeLanguages[size];
    return enabledLangs
      .map((code) => {
        const lang = AVAILABLE_LANGUAGES.find((l) => l.code === code);
        return lang ? { code: lang.code, label: lang.label } : null;
      })
      .filter((l) => l !== null) as { code: string; label: string }[];
  };

  const renderPrintSizeBlock = (
    size: "card" | "a5" | "a4",
    titleKey: string,
    sizeOptions: LocationReviewRequestPrintsSizeOptions,
    previewRef: React.RefObject<HTMLDivElement>,
    qrRef: React.RefObject<HTMLCanvasElement>
  ) => {
    return (
      <div className="print-size-block" key={size}>
        <div className="size-header">
          <div className="header-left">
            <h3 className="size-title">{t(titleKey)}</h3>
            <LanguageTagsDisplay
              languages={getLanguageTags(sizeOptions, size)}
            />
          </div>
          <div className="header-right">
            <button
              className="btn btn-border"
              onClick={() => setEditModal({ isOpen: true, size })}
            >
              <i className="icon icon-edit"></i>
              {t("edit")}
            </button>
            <button
              className="btn btn-blue download-button"
              onClick={() => handleDownloadQR(size, previewRef)}
              disabled={downloading[size]}
            >
              <i className="icon icon-saveload icon-saveload-blue"></i>
              {downloading[size] ? t("downloading") : t("download")}
            </button>
          </div>
        </div>
        <div className="preview-wrapper" ref={previewRef}>
          <ReviewRequestPrintPreview
            size={size}
            qrCodeUrl={qrCodeUrl}
            options={getFilteredMessages(sizeOptions, size)}
            qrRef={qrRef}
          />
        </div>
      </div>
    );
  };

  if (isLoading || !printsData || !locationData) {
    return (
      <div className="right-content review-request-prints-page">
        <div className="loading">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="right-content review-request-prints-page">
      <div className="back-top-bar">
        <NavLink
          to={`/location/${locationId}${
            organizationId ? `/organization/${organizationId}` : ""
          }`}
          className="back-link"
        >
          <i className="icons icon-back"></i>
          {t("back_to_facility")}
        </NavLink>
      </div>
      <div className="top-title-bar">
        {!!locationData && <PageTitle text={locationData.name} />}
      </div>

      <div className="page-description">{t("page_description")}</div>

      <div className="prints-preview-section">
        {renderPrintSizeBlock(
          "card",
          "card_size",
          printsData.card_size_options,
          cardPreviewRef,
          cardQrRef
        )}

        {renderPrintSizeBlock(
          "a5",
          "a5_size",
          printsData.a_five_size_options,
          a5PreviewRef,
          a5QrRef
        )}

        {renderPrintSizeBlock(
          "a4",
          "a4_size",
          printsData.a_four_size_options,
          a4PreviewRef,
          a4QrRef
        )}
      </div>

      {/* Edit Languages Modal */}
      {editModal.size && (
        <EditLanguagesModal
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, size: null })}
          selectedLanguages={sizeLanguages[editModal.size]}
          onSave={(languages) => {
            if (editModal.size) {
              handleLanguageSave(editModal.size, languages);
            }
          }}
          sizeName={t(
            editModal.size === "card"
              ? "card_size"
              : editModal.size === "a5"
              ? "a5_size"
              : "a4_size"
          )}
        />
      )}
    </div>
  );
};

export default withRouter(ReviewRequestPrintsPage);
